/**
 * 流式对话：把 `POST /chat/completions` 的 SSE 响应翻译成一组回调。
 *
 * 三个刻意的设计：
 * 1. **只用一个 `TextDecoder`，且带 `{stream: true}`**。一个中文字符是 3 字节，
 *    网络分片完全可能在字符中间切开；每个 chunk 新建一个 decoder 会得到一串
 *    U+FFFD（"锟斤拷"式乱码）。`decodeByteStream` 就是为了让这一点可被测试覆盖。
 * 2. **不做节流**。delta 到了就回调；节流是渲染层的事（`chat-store` 用 40ms 合并），
 *    在这里丢帧会让"已落库的正文"和"屏幕上的正文"永久不一致。
 * 3. **40101 只在这里重放**，不在 `api-client` 里：只有这里知道"还没渲染任何内容，
 *    重放是安全的"。
 */

import {
  ErrorCode,
  consumeSseStream,
  validateSsePayload,
  type SseDeltaPayload,
  type SseDonePayload,
  type SseErrorPayload,
  type SseEvent,
  type SseMetaPayload,
  type SseParseErrorInfo,
  type SseRetractPayload,
  type SseUsagePayload,
} from '@spirit/contracts';

import { apiErrorFromResponse, apiFetchRaw, isApiError, refreshAccessToken } from './api-client';

export interface ChatStreamInput {
  conversationId?: string;
  message: string;
  domain?: string;
  ageBand?: string;
}

export interface ChatStreamCallbacks {
  onMeta: (payload: SseMetaPayload) => void;
  onDelta: (payload: SseDeltaPayload) => void;
  onUsage: (payload: SseUsagePayload) => void;
  /**
   * 服务端判定"这条回答不能留在用户面前"，整条撤回。
   *
   * 它不是错误：回答已经**完整**下发过（前面的 delta 都在），撤回发生在收尾阶段，
   * 之后仍会有一个 `done` 帧，但没有 `usage`（撤回的回答不计费）。
   * 界面要做的只有一件事：把正文清掉，换上 `safety.publicMessage` 那张提示卡。
   */
  onRetract: (payload: SseRetractPayload) => void;
  onDone: (payload: SseDonePayload) => void;
  onError: (payload: SseErrorPayload) => void;
  onParseError?: (info: SseParseErrorInfo) => void;
}

export interface StreamChatOptions {
  input: ChatStreamInput;
  signal: AbortSignal;
  callbacks: ChatStreamCallbacks;
}

/**
 * 字节读取器的最小接口。
 *
 * 不直接用 `ReadableStreamDefaultReader` 是为了让测试能塞一个"每次返回固定字节数、
 * 故意从多字节字符中间切开"的假实现，而不必造一个真的 `Response`。
 */
export interface ByteReader {
  read(): Promise<{ done: boolean; value?: Uint8Array }>;
  releaseLock?(): void;
}

/**
 * 字节流 → 字符串流。
 *
 * 先 `{stream: true}` 逐块解码，最后用一次无参 `decode()` 冲出解码器里滞留的
 * 半个字符。`finally` 里释放锁，避免调用方提前退出时把 reader 锁死。
 */
export async function* decodeByteStream(reader: ByteReader, decoder: TextDecoder): AsyncGenerator<string> {
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      if (value === undefined || value.byteLength === 0) {
        continue;
      }
      yield decoder.decode(value, { stream: true });
    }
    const tail = decoder.decode();
    if (tail.length > 0) {
      yield tail;
    }
  } finally {
    reader.releaseLock?.();
  }
}

function isEventStream(response: Response): boolean {
  const contentType = response.headers.get('content-type');
  return contentType !== null && contentType.toLowerCase().includes('text/event-stream');
}

/**
 * 把一帧事件收窄成具体载荷。
 *
 * `SseEvent` 的 `data` 是一个联合类型，**没有**与 `event` 名做判别关联，
 * 所以不能靠类型断言取载荷，必须走 `validateSsePayload` 运行时校验。
 */
function payloadOf<K extends SseEvent['event']>(event: SseEvent, name: K): unknown | null {
  if (event.event !== name) {
    return null;
  }
  return validateSsePayload(name, event.data);
}

function dispatchEvent(event: SseEvent, callbacks: ChatStreamCallbacks): void {
  switch (event.event) {
    case 'meta': {
      const payload = payloadOf(event, 'meta') as SseMetaPayload | null;
      if (payload !== null) {
        callbacks.onMeta(payload);
      }
      return;
    }
    case 'delta': {
      const payload = payloadOf(event, 'delta') as SseDeltaPayload | null;
      if (payload !== null) {
        callbacks.onDelta(payload);
      }
      return;
    }
    case 'usage': {
      const payload = payloadOf(event, 'usage') as SseUsagePayload | null;
      if (payload !== null) {
        callbacks.onUsage(payload);
      }
      return;
    }
    case 'retract': {
      const payload = payloadOf(event, 'retract') as SseRetractPayload | null;
      if (payload !== null) {
        callbacks.onRetract(payload);
      }
      return;
    }
    case 'done': {
      const payload = payloadOf(event, 'done') as SseDonePayload | null;
      if (payload !== null) {
        callbacks.onDone(payload);
      }
      return;
    }
    case 'error': {
      const payload = payloadOf(event, 'error') as SseErrorPayload | null;
      if (payload !== null) {
        callbacks.onError(payload);
      }
      return;
    }
    default:
      return;
  }
}

async function consume(response: Response, callbacks: ChatStreamCallbacks): Promise<number> {
  if (response.body === null) {
    callbacks.onError({ code: ErrorCode.INTERNAL, message: '响应没有可读取的流（body 为空）' });
    return 0;
  }

  const decoder = new TextDecoder('utf-8');
  const reader = response.body.getReader();

  const result = await consumeSseStream(decodeByteStream(reader, decoder), {
    onEvent: (event) => {
      void dispatchEvent(event, callbacks);
    },
    ...(callbacks.onParseError !== undefined ? { onParseError: callbacks.onParseError } : {}),
  });

  return result.events;
}

/**
 * 发起一次流式问答，直到 `done` / `error` / 流结束 / 被 abort。
 *
 * 中止语义与 fetch 一致：`signal` 触发后 `reader.read()` 以 `AbortError` 拒绝，
 * 调用方据此区分"用户点了停止"和"回答正常结束"——这两种情况在 UI 上完全不同。
 */
export async function streamChatCompletion(options: StreamChatOptions): Promise<void> {
  const { input, signal, callbacks } = options;

  const body = {
    ...(input.conversationId !== undefined ? { conversationId: input.conversationId } : {}),
    message: input.message,
    ...(input.domain !== undefined ? { domain: input.domain } : {}),
    ...(input.ageBand !== undefined ? { ageBand: input.ageBand } : {}),
    // `stream: false` 会被服务端以 403 + 40301 拒绝，见契约 §5。
    stream: true,
    // 附件上传未实现，契约要求显式传空数组而不是省略字段。
    attachments: [],
  };

  for (let attempt = 0; attempt < 2; attempt += 1) {
    const response = await apiFetchRaw('/chat/completions', {
      method: 'POST',
      body,
      signal,
      headers: { Accept: 'text/event-stream' },
    });

    if (isEventStream(response)) {
      await consume(response, callbacks);
      return;
    }

    const failure = await apiErrorFromResponse(response);
    // 令牌过期时重放一次是安全的：此时一个 delta 都还没渲染，重放不会重复显示。
    if (failure.code === ErrorCode.TOKEN_EXPIRED && attempt === 0) {
      await refreshAccessToken();
      continue;
    }
    throw failure;
  }

  throw new Error('streamChatCompletion: 重试后仍未获得可读的 SSE 流');
}

// 让上层（chat-store / 组件）只需从一个模块取全部流式相关工具。
export { isAbortError, isApiError } from './api-client';
export type { SseDeltaPayload, SseDonePayload, SseErrorPayload, SseMetaPayload, SseRetractPayload, SseUsagePayload };
